import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import winnerLogo from "@/assets/winnerLogo.svg";
import closeModal from "@/assets/closeModal.svg";
import winSound from "../../assets/winning sound and cheering.mp3";
import winningGif from "../../assets/merv_win.gif";

interface WinModalProps {
  open: boolean;
  onClose: () => void;
  ticketType: 'golden' | 'diamond';
  ticketCode?: string;
  winMessage?: string;
  prizeAmount?: number;
  hasWin: boolean;
  onScreenshot?: () => void;
}

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(8px);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.5rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(145deg, rgba(5, 5, 24, 0.98), rgba(10, 10, 40, 0.98));
  border-radius: 24px;
  max-width: 650px;
  width: 90%;
  position: relative;
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  padding: 2rem;
  border: 3px solid transparent;
  background-image: 
    linear-gradient(145deg, rgba(5, 5, 24, 0.98), rgba(10, 10, 40, 0.98)),
    linear-gradient(45deg, #FFD700, #FF6B35, #8A2BE2, #40E0D0);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  box-shadow: 
    0 25px 80px rgba(0, 0, 0, 0.8),
    0 0 60px rgba(255, 215, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  animation: modalAppear 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Better screenshot quality */
  &[data-modal-content="true"] {
    /* Ensure crisp rendering for screenshots */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    transform: translateZ(0); /* Force hardware acceleration */
  }
  
  @keyframes modalAppear {
    0% {
      opacity: 0;
      transform: scale(0.7) translateY(50px);
    }
    50% {
      transform: scale(1.05) translateY(-10px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
   
    border-radius: 27px;
    z-index: -1;
    animation: borderGlow 3s ease-in-out infinite alternate;
  }
  
  @keyframes borderGlow {
    0% {
      filter: blur(2px) brightness(1);
    }
    100% {
      filter: blur(4px) brightness(1.3);
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 75%;
    gap: 1rem;
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    max-width: 85%;
    padding: 1rem;
    gap: 0.75rem;
    border-radius: 20px;
    
    &::before {
      border-radius: 23px;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  backdrop-filter: blur(10px);

  img {
    width: 22px;
    height: 22px;
    filter: brightness(1.2);
    transition: transform 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.1));
    border-color: rgba(255, 255, 255, 0.4);
    transform: scale(1.1) rotate(90deg);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
    
    img {
      transform: scale(1.1);
    }
  }

  &:active {
    transform: scale(0.95) rotate(90deg);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    top: 0.75rem;
    right: 0.75rem;
    
    img {
      width: 16px;
      height: 16px;
    }
  }
`;

const ScreenshotButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 4rem;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.9), rgba(56, 142, 60, 0.8));
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  backdrop-filter: blur(10px);
  color: white;
  font-size: 18px;

  &:hover {
    background: linear-gradient(135deg, rgba(76, 175, 80, 1), rgba(56, 142, 60, 0.9));
    border-color: rgba(76, 175, 80, 0.6);
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    top: 0.75rem;
    right: 3rem;
    font-size: 14px;
  }
`;

const WinModal: React.FC<WinModalProps> = ({
  open,
  onClose,
  ticketType,
  ticketCode,
  winMessage,
  prizeAmount,
  hasWin,
  onScreenshot
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (open && hasWin) {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          console.log('Audio autoplay was prevented');
        });
      }
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, [open, hasWin]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTicketTypeColor = () => {
    return ticketType === 'golden' 
      ? 'linear-gradient(135deg, #FFD700, #FF8C00)' 
      : 'linear-gradient(135deg, #40E0D0, #8A2BE2)';
  };

  const getTicketTypeEmoji = () => {
    return ticketType === 'golden' ? '🏆' : '💎';
  };

  const getTicketTypeName = () => {
    return ticketType === 'golden' ? 'GOLDEN TICKET' : 'DIAMOND TICKET';
  };

  return (
    <>
      <audio ref={audioRef} src={winSound} />
      <Modal isOpen={open} onClick={handleOverlayClick}>
        <ModalContent data-modal-content="true">
          <CloseButton onClick={onClose}>
            <img src={closeModal} alt="Close" />
          </CloseButton>

          {hasWin && onScreenshot && (
            <ScreenshotButton onClick={onScreenshot} title="Take Screenshot">
              📸
            </ScreenshotButton>
          )}

          {/* Left Section - Animation */}
          <div
            style={{
              height:
                window.innerWidth > 1200
                  ? '340px'
                  : window.innerWidth > 900
                    ? '260px'
                    : window.innerWidth > 768
                      ? '200px'
                      : window.innerWidth > 480
                        ? '160px'
                        : '130px',
              width:
                window.innerWidth > 1200
                  ? '340px'
                  : window.innerWidth > 900
                    ? '260px'
                    : window.innerWidth > 768
                      ? '200px'
                      : '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px',
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2))',
              boxShadow: ticketType === 'golden' 
                ? '0 0 40px rgba(255, 215, 0, 0.4), inset 0 0 20px rgba(255, 215, 0, 0.1)' 
                : '0 0 40px rgba(64, 224, 208, 0.4), inset 0 0 20px rgba(64, 224, 208, 0.1)',
              border: '2px solid',
              borderColor: ticketType === 'golden' ? 'rgba(255, 215, 0, 0.3)' : 'rgba(64, 224, 208, 0.3)'
            }}
          >
            {/* Animated border */}
            <div
              style={{
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                right: '-2px',
                bottom: '-2px',
                background: ticketType === 'golden'
                  ? 'linear-gradient(45deg, #FFD700, #FF8C00, #FFD700, #FF8C00)'
                  : 'linear-gradient(45deg, #40E0D0, #8A2BE2, #40E0D0, #8A2BE2)',
                borderRadius: '22px',
                zIndex: -1,
                animation: 'rotateBorder 4s linear infinite'
              }}
            />
            
            {/* Floating particles */}
            <div
              style={{
                position: 'absolute',
                top: '10%',
                left: '10%',
                width: '8px',
                height: '8px',
                background: ticketType === 'golden' ? '#FFD700' : '#40E0D0',
                borderRadius: '50%',
                animation: 'float1 3s ease-in-out infinite',
                boxShadow: `0 0 10px ${ticketType === 'golden' ? '#FFD700' : '#40E0D0'}`
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '70%',
                right: '15%',
                width: '6px',
                height: '6px',
                background: ticketType === 'golden' ? '#FF8C00' : '#8A2BE2',
                borderRadius: '50%',
                animation: 'float2 4s ease-in-out infinite reverse',
                boxShadow: `0 0 8px ${ticketType === 'golden' ? '#FF8C00' : '#8A2BE2'}`
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: '30%',
                right: '20%',
                width: '4px',
                height: '4px',
                background: '#FFFFFF',
                borderRadius: '50%',
                animation: 'float3 5s ease-in-out infinite',
                boxShadow: '0 0 6px #FFFFFF'
              }}
            />

            <img
              src={winningGif}
              alt="Congratulations"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '18px',
                filter: 'brightness(1.1) contrast(1.1)'
              }}
            />

            <style>
              {`
                @keyframes rotateBorder {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes float1 {
                  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.7; }
                  50% { transform: translateY(-15px) translateX(10px); opacity: 1; }
                }
                @keyframes float2 {
                  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.8; }
                  50% { transform: translateY(-10px) translateX(-8px); opacity: 1; }
                }
                @keyframes float3 {
                  0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.6; }
                  50% { transform: translateY(-12px) translateX(5px); opacity: 1; }
                }
              `}
            </style>
          </div>

          {/* Right Section */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: window.innerWidth > 480 ? '1.5rem' : '1rem',
            padding: window.innerWidth > 480 ? '1rem' : '0.5rem',
            flex: 1,
            width: '100%'
          }}>
            {hasWin ? (
              <>
                {/* Success Content */}
                <div style={{
                  display: 'flex',
                  flexDirection: window.innerWidth > 768 ? 'row' : 'row',
                  gap: window.innerWidth > 480 ? '1rem' : '0.75rem',
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: window.innerWidth > 768 ? 'auto' : '100%',
                    maxWidth: window.innerWidth > 768 ? '200px' : '300px',
                    background: getTicketTypeColor(),
                    borderRadius: window.innerWidth > 480 ? '16px' : '12px',
                    padding: window.innerWidth > 480 ? '1.5rem' : '1rem',
                    color: 'white',
                    boxShadow: ticketType === 'golden' 
                      ? '0 8px 25px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)' 
                      : '0 8px 25px rgba(64, 224, 208, 0.4), 0 0 30px rgba(64, 224, 208, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    animation: 'cardPulse 2s ease-in-out infinite alternate',
                    textAlign: 'center'
                  }}>
                    {/* Card shine effect */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                      animation: 'shine 3s ease-in-out infinite',
                      zIndex: 1
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <h3 style={{
                        fontSize: window.innerWidth > 768 ? '24px' : window.innerWidth > 480 ? '22px' : '18px',
                        margin: '0 0 1rem 0',
                        fontWeight: 'bold',
                        textShadow: '0 0 15px rgba(255,255,255,0.5)',
                        animation: 'textGlow 2s ease-in-out infinite alternate'
                      }}>{getTicketTypeEmoji()} YOU WIN!</h3>
                      <div>
                        <p style={{
                          fontSize: window.innerWidth > 480 ? '13px' : '12px',
                          color: 'rgba(255, 255, 255, 0.9)',
                          margin: '0 0 0.5rem 0',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>{getTicketTypeName()}</p>
                        <p style={{
                          fontSize: window.innerWidth > 768 ? '16px' : window.innerWidth > 480 ? '15px' : '13px',
                          margin: '0',
                          fontWeight: 'bold',
                          wordBreak: 'break-all',
                          textShadow: '0 0 10px rgba(0,0,0,0.5)',
                          lineHeight: 1.3
                        }}>{ticketCode}</p>
                      </div>
                    </div>
                  </div>

                  {prizeAmount && prizeAmount > 0 && (
                    <div style={{
                      width: window.innerWidth > 768 ? 'auto' : '100%',
                      maxWidth: window.innerWidth > 768 ? '200px' : '300px',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                      borderRadius: window.innerWidth > 480 ? '16px' : '12px',
                      padding: window.innerWidth > 480 ? '1.5rem' : '1rem',
                      backdropFilter: 'blur(15px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                    }}>
                      {/* Prize card glow */}
                      <div style={{
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        animation: 'prizeGlow 4s ease-in-out infinite',
                        zIndex: 1
                      }} />
                      
                      <div style={{ position: 'relative', zIndex: 2 }}>
                        <h3 style={{
                          fontSize: window.innerWidth > 480 ? '17px' : '15px',
                          color: 'rgba(255, 255, 255, 0.95)',
                          margin: '0 0 0.75rem 0',
                          fontWeight: '600',
                          letterSpacing: '0.5px'
                        }}>💰 Prize Amount</h3>
                        <h2 style={{
                          fontSize: window.innerWidth > 768 ? '32px' : window.innerWidth > 480 ? '28px' : '24px',
                          margin: '0',
                          background: 'linear-gradient(135deg, #fff, #FFD700, #fff)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: 'bold',
                          textShadow: '0 0 20px rgba(255,215,0,0.8)',
                          animation: 'prizeShine 3s ease-in-out infinite'
                        }}>$ {prizeAmount}</h2>
                      </div>
                    </div>
                  )}
                </div>

                {winMessage && (
                  <div style={{
                    width: '100%',
                    maxWidth: '400px',
                    fontSize: window.innerWidth > 768 ? '18px' : window.innerWidth > 480 ? '16px' : '14px',
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    // padding: window.innerWidth > 480 ? '1.25rem' : '1rem',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))',
                    borderRadius: '16px',
                    backdropFilter: 'blur(15px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    animation: 'messageFloat 3s ease-in-out infinite',
                    margin: '0 auto'
                  }}>
                    {/* Message background effect */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent)',
                      animation: 'messageShimmer 4s ease-in-out infinite',
                      zIndex: 1
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 2, textShadow: '0 0 15px rgba(255,255,255,0.3)' }}>
                      {winMessage}
                    </div>
                  </div>
                )}

                <style>
                  {`
                    @keyframes cardPulse {
                      0% { transform: scale(1); }
                      100% { transform: scale(1.02); }
                    }
                    @keyframes shine {
                      0% { left: -100%; }
                      50% { left: 100%; }
                      100% { left: 100%; }
                    }
                    @keyframes textGlow {
                      0% { text-shadow: 0 0 15px rgba(255,255,255,0.5); }
                      100% { text-shadow: 0 0 25px rgba(255,255,255,0.8), 0 0 35px rgba(255,215,0,0.6); }
                    }
                    @keyframes prizeGlow {
                      0% { transform: rotate(0deg) scale(1); opacity: 0.3; }
                      50% { transform: rotate(180deg) scale(1.1); opacity: 0.1; }
                      100% { transform: rotate(360deg) scale(1); opacity: 0.3; }
                    }
                    @keyframes prizeShine {
                      0% { background-position: -200% 0; }
                      100% { background-position: 200% 0; }
                    }
                    @keyframes messageFloat {
                      0%, 100% { transform: translateY(0px); }
                      50% { transform: translateY(-3px); }
                    }
                    @keyframes messageShimmer {
                      0% { transform: translateX(-100%) skewX(-15deg); }
                      50% { transform: translateX(100%) skewX(-15deg); }
                      100% { transform: translateX(100%) skewX(-15deg); }
                    }
                  `}
                </style>
              </>
            ) : (
              /* No Win Content */
              <div style={{
                width: '100%',
                maxWidth: '400px',
                margin: '0 auto',
                textAlign: 'center',
                color: 'white',
                padding: window.innerWidth > 480 ? '2.5rem' : '2rem',
                background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 107, 53, 0.05))',
                borderRadius: '20px',
                border: '1px solid rgba(255, 107, 53, 0.3)',
                backdropFilter: 'blur(15px)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Background animation */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle, rgba(255,107,53,0.1) 0%, transparent 70%)',
                  animation: 'encourageGlow 3s ease-in-out infinite',
                  zIndex: 1
                }} />
                
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <h2 style={{
                    fontSize: window.innerWidth > 768 ? '28px' : window.innerWidth > 480 ? '24px' : '20px',
                    margin: '0 0 1.5rem 0',
                    color: '#FF6B35',
                    fontWeight: 'bold',
                    textShadow: '0 0 20px rgba(255, 107, 53, 0.6)',
                    animation: 'encouragePulse 2s ease-in-out infinite alternate'
                  }}>🎯 Keep Hunting!</h2>
                  <p style={{
                    fontSize: window.innerWidth > 480 ? '16px' : '14px',
                    opacity: 0.9,
                    lineHeight: 1.6,
                    fontWeight: '500',
                    textShadow: '0 0 10px rgba(255,255,255,0.3)',
                    animation: 'encourageFloat 4s ease-in-out infinite',
                    margin: '0 auto',
                    maxWidth: '300px'
                  }}>
                    {ticketType === 'golden' ? 
                      "This wasn't the winning Golden Ticket, but don't give up! The treasure is still out there waiting to be claimed! ✨" :
                      "These Diamond codes didn't match this time, but keep searching! Your diamond empire awaits! 💎"
                    }
                  </p>
                  
                  {/* Decorative elements */}
                  <div style={{
                    position: 'absolute',
                    top: '15%',
                    left: '10%',
                    width: '6px',
                    height: '6px',
                    background: '#FFD700',
                    borderRadius: '50%',
                    animation: 'sparkle1 2s ease-in-out infinite',
                    boxShadow: '0 0 10px #FFD700'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '70%',
                    right: '15%',
                    width: '4px',
                    height: '4px',
                    background: '#FF6B35',
                    borderRadius: '50%',
                    animation: 'sparkle2 3s ease-in-out infinite',
                    boxShadow: '0 0 8px #FF6B35'
                  }} />
                </div>

                <style>
                  {`
                    @keyframes encourageGlow {
                      0%, 100% { transform: rotate(0deg) scale(1); opacity: 0.3; }
                      50% { transform: rotate(180deg) scale(1.1); opacity: 0.1; }
                    }
                    @keyframes encouragePulse {
                      0% { transform: scale(1); text-shadow: 0 0 20px rgba(255, 107, 53, 0.6); }
                      100% { transform: scale(1.05); text-shadow: 0 0 30px rgba(255, 107, 53, 0.9); }
                    }
                    @keyframes encourageFloat {
                      0%, 100% { transform: translateY(0px); }
                      50% { transform: translateY(-5px); }
                    }
                    @keyframes sparkle1 {
                      0%, 100% { opacity: 0.3; transform: scale(1); }
                      50% { opacity: 1; transform: scale(1.5); }
                    }
                    @keyframes sparkle2 {
                      0%, 100% { opacity: 0.4; transform: scale(1) rotate(0deg); }
                      50% { opacity: 1; transform: scale(1.3) rotate(180deg); }
                    }
                  `}
                </style>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default WinModal;